import { ArrowLeft } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { EmailAttachmentFilter, useEmailThread, useEmailThreads } from '@/models/internalEmail'
import { EmailFilters } from './EmailFilters'
import { EmailThreadList } from './EmailThreadList'
import { EmailThreadView } from './EmailThreadView'

/**
 * All emails, Gmail-style: threads on the left, the whole selected thread on
 * the right. Every filter and the open thread live in the query string, so a
 * search or a specific thread is linkable.
 */
export function EmailsIndex() {
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''
  const fromEmail = searchParams.get('from') ?? ''
  const hasAttachments = (searchParams.get('attachments') ??
    EmailAttachmentFilter.ANY) as EmailAttachmentFilter
  // A hand-edited ?page=abc must not reach the pager as NaN.
  const parsedPage = Number(searchParams.get('page'))
  const page = Number.isInteger(parsedPage) ? Math.max(1, parsedPage) : 1
  const threadKey = searchParams.get('thread')

  function update(changes: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setSearchParams(next)
  }

  // Changing a filter reshuffles the pages, so the old page number no longer
  // means anything — drop back to the first page whenever a filter moves. The
  // open thread goes with it: it may not be in the new results, and on a phone
  // it would otherwise stay as the only visible pane.
  function updateFilter(changes: Record<string, string | undefined>) {
    update({ ...changes, page: undefined, thread: undefined })
  }

  const { data, isLoading } = useEmailThreads({ q, fromEmail, hasAttachments, page })
  const { data: thread, isLoading: threadLoading } = useEmailThread(threadKey)

  return (
    <>
      <Helmet>
        <title>All Emails</title>
      </Helmet>
      {/* Not flex-1: its 0% basis resolves to content height in the min-height shell, so the panes would grow. */}
      <div className="flex min-h-96 w-full grow basis-0 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold">All Emails</h1>
          <EmailFilters
            q={q}
            fromEmail={fromEmail}
            hasAttachments={hasAttachments}
            onQ={(value) => updateFilter({ q: value || undefined })}
            onFromEmail={(value) => updateFilter({ from: value || undefined })}
            onHasAttachments={(value) =>
              updateFilter({
                attachments: value === EmailAttachmentFilter.ANY ? undefined : value,
              })
            }
          />
        </div>

        {/* Two panes side by side on a desktop; on a phone only one of them is on screen at a time. */}
        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(20rem,26rem)_1fr]">
          {/* min-w-0 on both panes: a grid column won't shrink past its content, so rows can truncate. */}
          <div className={cn('flex min-h-0 min-w-0 flex-col', threadKey && 'max-lg:hidden')}>
            <EmailThreadList
              page={data}
              isLoading={isLoading}
              pageNumber={page}
              selectedKey={threadKey}
              onSelect={(key) => update({ thread: key })}
              onPage={(next) => update({ page: next > 1 ? String(next) : undefined })}
            />
          </div>
          <div className={cn('flex min-h-0 min-w-0 flex-col gap-2', !threadKey && 'max-lg:hidden')}>
            <Button
              variant="outline"
              size="sm"
              className="w-fit lg:hidden"
              onClick={() => update({ thread: undefined })}
            >
              <ArrowLeft className="size-3.5" />
              All threads
            </Button>
            {/* The view is h-full, so it needs a parent sized by the row rather than by its own content. */}
            <div className="min-h-0 flex-1">
              <EmailThreadView threadKey={threadKey} thread={thread} isLoading={threadLoading} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
